import React, { useState, useEffect } from "react";
import { Default } from "../../components/default/Default";
import { McaDrawer } from "../../components/mca";
import { showToaster } from "../../common/common";
import PostRequest from "../../services/PostRequest";
import ErrorTable from "./ErrorTable";
import moment from "moment";
import {
  SOURCE_OPTS, SEV_OPTS, STATUS_OPTS, DATE_OPTS,
  SOURCE_LABEL, SEVERITY_LABEL, STATUS_LABEL, STATUS_CLASS,
} from "../../data/errorLogConstants";

export const ErrorLogs = () => {
  const [filters, setFilters] = useState({ source: "all", severity: "all", status: "all", range: "all", q: "" });
  const [searchInput, setSearchInput] = useState("");
  const [selectedError, setSelectedError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [statusSaving, setStatusSaving] = useState(false);

  // Debounce the free-text search before it hits the server.
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, q: searchInput.trim() })), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const open = (e) => {
    setSelectedError(e);
    setDrawerOpen(true);
  };
  const close = () => {
    setDrawerOpen(false);
    setSelectedError(null);
  };

  // Manually mark the open row new/read/resolved; refresh the table afterwards.
  const markStatus = async (next) => {
    if (!selectedError) return;
    try {
      setStatusSaving(true);
      await PostRequest("/users/error/logs/status/", { id: selectedError.id, status: next });
      setSelectedError((cur) => (cur ? { ...cur, status: next } : cur));
      setReloadKey((k) => k + 1);
      showToaster(`Marked ${next}.`, "success");
    } catch (err) {
      showToaster(err.message, "error");
    } finally {
      setStatusSaving(false);
    }
  };

  const e = selectedError;
  const alertKind =
    e?.severity === "warning" ? "warning" : e?.severity === "info" ? "info" : "danger";

  return (
    <Default>
      <div className="mca-content">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "14px",
            flexWrap: "wrap",
            marginBottom: "16px",
          }}
        >
          <div>
            <h1 style={{ fontSize: "26px", fontWeight: "700", letterSpacing: "-0.02em" }}>
              Error logs
            </h1>
            <p style={{ color: "var(--mca-muted)", fontSize: "14px", margin: "6px 0 0" }}>
              API-submission &amp; scrape failures across your account. Click a row for details.
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
            <div className="mca-input-group" style={{ width: "210px" }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4-4" />
              </svg>
              <input
                className="mca-input"
                type="search"
                placeholder="Search ASIN, message…"
                value={searchInput}
                onChange={(ev) => setSearchInput(ev.target.value)}
                style={{ paddingTop: "8px", paddingBottom: "8px" }}
              />
            </div>
            <select
              className="mca-select"
              value={filters.source}
              onChange={(ev) => setFilters((f) => ({ ...f, source: ev.target.value }))}
              style={{ width: "auto" }}
            >
              {SOURCE_OPTS.map((o) => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
            <select
              className="mca-select"
              value={filters.severity}
              onChange={(ev) => setFilters((f) => ({ ...f, severity: ev.target.value }))}
              style={{ width: "auto" }}
            >
              {SEV_OPTS.map((o) => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
            <select
              className="mca-select"
              value={filters.status}
              onChange={(ev) => setFilters((f) => ({ ...f, status: ev.target.value }))}
              style={{ width: "auto" }}
            >
              {STATUS_OPTS.map((o) => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
            <select
              className="mca-select"
              value={filters.range}
              onChange={(ev) => setFilters((f) => ({ ...f, range: ev.target.value }))}
              style={{ width: "auto" }}
            >
              {DATE_OPTS.map((o) => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
          </div>
        </div>

        <ErrorTable filters={filters} reloadKey={reloadKey} onErrorClick={open} />
      </div>

      <McaDrawer
        open={drawerOpen}
        onClose={close}
        title={e?.error_title || e?.reason || "Error detail"}
        subtitle={e ? `${e.source || ""}${e.asin_code ? " · " + e.asin_code : ""}` : undefined}
        footer={
          <>
            <button className="mca-btn mca-btn--outline" onClick={close} type="button">
              Close
            </button>
            {e && e.status !== "read" && e.status !== "resolved" ? (
              <button className="mca-btn mca-btn--ghost" type="button" disabled={statusSaving}
                onClick={() => markStatus("read")}>
                Mark read
              </button>
            ) : null}
            {e && e.status !== "resolved" ? (
              <button className="mca-btn mca-btn--ghost" type="button" disabled={statusSaving}
                onClick={() => markStatus("resolved")}>
                Mark resolved
              </button>
            ) : (
              <button className="mca-btn mca-btn--ghost" type="button" disabled={statusSaving}
                onClick={() => markStatus("new")}>
                Reopen
              </button>
            )}
            {e?.collection ? (
              <a className="mca-btn mca-btn--primary" href={`/collections/${e.collection}/download`}>
                View results
              </a>
            ) : null}
          </>
        }
      >
        {e && (
          <>
            <div className={`mca-alert mca-alert--${alertKind}`} style={{ marginBottom: "18px" }}>
              <svg className="lead" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v5M12 16h.01" />
              </svg>
              <div className="mca-alert__body">
                {e.error_message || e.reason || e.error_title}
              </div>
            </div>

            <dl className="kv2" style={{ marginBottom: "20px" }}>
              <dt>Source</dt><dd>{SOURCE_LABEL[e.source] || e.source || "—"}</dd>
              <dt>Type</dt><dd>{e.type || "—"}</dd>
              <dt>Severity</dt><dd>{SEVERITY_LABEL[e.severity] || e.severity || "—"}</dd>
              <dt>Status</dt>
              <dd>
                <span className={`mca-badge mca-badge--${STATUS_CLASS[e.status] || "soft-muted"}`}>
                  {STATUS_LABEL[e.status] || e.status || "New"}
                </span>
              </dd>
              <dt>ASIN</dt><dd className="mca-cell-mono">{e.asin_code || "—"}</dd>
              <dt>Domain</dt><dd>{e.domain || "—"}</dd>
              <dt>Postcode</dt><dd>{e.postcode || "—"}</dd>
              <dt>Collection</dt><dd>{e.collection_name || "—"}</dd>
              <dt>HTTP status</dt><dd className="mca-cell-mono">{e.http_status || "—"}</dd>
              <dt>Request ID</dt><dd className="mca-cell-mono">{e.request_id || "—"}</dd>
              <dt>Time</dt><dd>{e.created_at ? moment(e.created_at).format("LLL") : "—"}</dd>
            </dl>

            {e.error_full_details ? (
              <div className="mca-code">
                <div className="mca-code__head">
                  <span className="mca-code__lang">details</span>
                </div>
                <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                  {e.error_full_details}
                </pre>
              </div>
            ) : null}
          </>
        )}
      </McaDrawer>
    </Default>
  );
};
